import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, MenuItem, Menu, MenuContent, MenuTrigger } from '@cad-arc';
import { FR_CREATE_EVENT_SUB_PATH, FR_EVENT_ROOT_PATH } from '../../../router/constants';
import { GeoPointLocation } from '../../../esri/types';
import { useUnitAssignment } from '../../../unit-assignment/unit-assignment-context';
import { CommonSize, getBasicCommonSize } from '../../../common/helpers/utils';
import { useBootstrapState } from '../../../bootstrap/bootstrap-provider';
import { AttributeOption, CallForServiceOption } from '@mark43/cad-api';

export function SelfInitiateButton({
    unitId,
    geoPointLocation,
    size,
}: {
    unitId?: number;
    geoPointLocation?: GeoPointLocation;
    size?: CommonSize;
}) {
    const unitAssignment = useUnitAssignment();
    const navigate = useNavigate();
    const { eventPanelOptions: eventOptions } = useBootstrapState();

    const selfInitiateCallForServiceOptions = unitAssignment?.selfInitiateCallForServiceOptions;
    const eventOriginOtherOptions = useMemo(
        () => eventOptions?.eventOriginSelfInitiateOptions.filter((option) => option.isOther),
        [eventOptions?.eventOriginSelfInitiateOptions]
    );

    const handleSelfInitiateOptionSelect = useCallback(
        (selfInitiateOptionId: number, isCallForService: boolean) => {
            const urlParam = isCallForService
                ? 'selfInitiateCallForServiceId'
                : 'eventOriginOtherOptionId';

            navigate(
                `${FR_EVENT_ROOT_PATH}/${FR_CREATE_EVENT_SUB_PATH}?${urlParam}=${selfInitiateOptionId}`,
                {
                    state: {
                        eventInitiatedFromMap: true,
                        unitId,
                        geoPointLocation,
                    },
                }
            );
        },
        [navigate, unitId, geoPointLocation]
    );

    const buildMenuItem = useCallback(
        (selfInitiateOption: CallForServiceOption | AttributeOption, isCallForService: boolean) => (
            <MenuItem
                key={selfInitiateOption.value}
                onSelect={() =>
                    handleSelfInitiateOptionSelect(selfInitiateOption.value, isCallForService)
                }
            >
                {selfInitiateOption.label}
            </MenuItem>
        ),
        [handleSelfInitiateOptionSelect]
    );

    return (
        <Menu isModal={false}>
            <MenuTrigger asChild>
                <Button trailingVisual="Open" size={getBasicCommonSize(size)}>
                    Self-initiate
                </Button>
            </MenuTrigger>
            <MenuContent>
                {selfInitiateCallForServiceOptions?.map((option) => buildMenuItem(option, true))}
                {eventOriginOtherOptions?.map((option) => buildMenuItem(option, false))}
            </MenuContent>
        </Menu>
    );
}
